import { useState } from "react";
import { Download, FileSpreadsheet, FileText, X, Calendar, Filter, Loader2, Sparkles, Baby } from "lucide-react";
import { axiosInstance } from "../../App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getLocalDateString } from "../../lib/utils";
import { downloadFileWithProgress } from "../../lib/downloadProgress";

const DataExportButton = ({ 
  dataType = "activities", // "activities" | "nutrition" | "daily-logs"
  children: childrenList = [],
  classes = [],
  title = "Export Data"
}) => {
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    format: "excel",
    start_date: "",
    end_date: "",
    child_id: "",
    class_name: "all"
  });

  const dataTypeConfig = {
    "activities": {
      label: "Aktiviti",
      gradient: "from-purple-500 to-violet-500",
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600"
    },
    "nutrition": {
      label: "Pemakanan",
      gradient: "from-orange-500 to-amber-500",
      iconBg: "bg-orange-100",
      iconColor: "text-orange-600"
    },
    "daily-logs": {
      label: "Log Harian",
      gradient: "from-blue-500 to-cyan-500",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600"
    }
  };

  const config = dataTypeConfig[dataType] || dataTypeConfig["activities"];

  const handleExport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("format", filters.format);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.child_id) params.append("child_id", filters.child_id);
      if (filters.class_name && filters.class_name !== "all") params.append("class_name", filters.class_name);

      const extension = filters.format === "pdf" ? "pdf" : "xlsx";
      const filename = `Laporan_${config.label}_${getLocalDateString()}.${extension}`;

      await downloadFileWithProgress({
        label: `Laporan ${config.label} (${filters.format.toUpperCase()})`,
        filename,
        request: (onDownloadProgress) => axiosInstance.get(`/export-data/${dataType}?${params.toString()}`, {
          responseType: 'blob',
          onDownloadProgress
        })
      });

      toast.success(`${config.label} berjaya di-export!`);
      setShowDialog(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Ralat semasa export data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowDialog(true)}
        className="gap-2 border-2 border-gray-300 hover:border-gray-400 rounded-xl font-semibold"
        data-testid={`export-${dataType}-btn`}
      >
        <Download className="w-4 h-4" />
        Export
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md rounded-3xl border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${config.iconBg} flex items-center justify-center`}>
                <Download className={`w-5 h-5 ${config.iconColor}`} />
              </div>
              <div>
                <span className={`font-bold bg-gradient-to-r ${config.gradient} bg-clip-text text-transparent`}>
                  Export {config.label}
                </span>
                <p className="text-xs text-gray-500 font-normal mt-0.5">Pilih filter dan format</p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Format Selection */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                Format
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFilters({...filters, format: "excel"})}
                  data-testid="format-excel"
                  className={`flex-1 p-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-3 ${
                    filters.format === "excel" 
                      ? "border-green-500 bg-green-50 shadow-lg shadow-green-100" 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <FileSpreadsheet className={`w-6 h-6 ${filters.format === "excel" ? "text-green-600" : "text-gray-400"}`} />
                  <span className={`font-semibold ${filters.format === "excel" ? "text-green-700" : "text-gray-500"}`}>Excel</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFilters({...filters, format: "pdf"})}
                  data-testid="format-pdf"
                  className={`flex-1 p-4 rounded-2xl border-2 transition-all flex items-center justify-center gap-3 ${
                    filters.format === "pdf" 
                      ? "border-red-500 bg-red-50 shadow-lg shadow-red-100" 
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <FileText className={`w-6 h-6 ${filters.format === "pdf" ? "text-red-600" : "text-gray-400"}`} />
                  <span className={`font-semibold ${filters.format === "pdf" ? "text-red-700" : "text-gray-500"}`}>PDF</span>
                </button>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Julat Tarikh
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Dari</label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                    data-testid="start-date"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Hingga</label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                    data-testid="end-date"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Class Filter */}
            {classes.length > 0 && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Filter className="w-4 h-4 text-indigo-500" />
                  Kelas
                </label>
                <select
                  value={filters.class_name}
                  onChange={(e) => setFilters({...filters, class_name: e.target.value})}
                  data-testid="class-filter"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all font-medium"
                >
                  <option value="all">Semua Kelas</option>
                  {classes.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Child Filter */}
            {childrenList.length > 0 && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Baby className="w-4 h-4 text-pink-500" />
                  Kanak-kanak (Pilihan)
                </label>
                <select
                  value={filters.child_id}
                  onChange={(e) => setFilters({...filters, child_id: e.target.value})}
                  data-testid="child-filter"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all font-medium"
                >
                  <option value="">Semua Kanak-kanak</option>
                  {childrenList.map(child => (
                    <option key={child.id} value={child.id}>{child.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowDialog(false)}
              className="flex-1 rounded-xl border-2 font-semibold"
              data-testid="cancel-export"
            >
              Batal
            </Button>
            <Button
              onClick={handleExport}
              disabled={loading}
              className={`flex-1 bg-gradient-to-r ${config.gradient} text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all`}
              data-testid="confirm-export"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Export {filters.format.toUpperCase()}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DataExportButton;
